import { Host, Picker } from '@expo/ui';
import { BellOffIcon, BellIcon } from 'lucide-react-native';
import * as React from 'react';
import { Linking, Platform, Pressable, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import type { Event } from '@/lib/events';
import {
  reminderOptionStates,
  type ReminderIntervalMinutes,
  type ReminderOptionState,
} from '@sixam/shared/notification-core';
import {
  cancelLocalEventReminder,
  fetchMyEventReminder,
  scheduleLocalEventReminder,
  type EventReminder,
} from '@/lib/notifications';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';
import { useCurrentTime } from '@/lib/dev-clock';

type EventReminderPanelProps = Readonly<{
  error?: string | null;
  isLoading?: boolean;
  onCancelReminder?: () => void;
  onSaveReminder?: (intervalMinutes: ReminderIntervalMinutes) => void;
  /** Every lead time, each flagged with whether it can still be set. */
  options?: readonly ReminderOptionState[];
  reminder?: EventReminder | null;
  /** Event start, used to preview the exact time a choice would fire. */
  startsAt?: string;
}>;

function formatRemindAt(value: string | number | Date): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString([], {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    weekday: 'short',
  });
}

export function EventReminderPanel({
  error,
  isLoading,
  onCancelReminder,
  onSaveReminder,
  options = [],
  reminder,
  startsAt,
}: EventReminderPanelProps) {
  const enabled = reminder?.status === 'scheduled';
  const firstAvailable = options.find((option) => option.available);
  const [selectedMinutes, setSelectedMinutes] = React.useState<ReminderIntervalMinutes | null>(
    null
  );

  // Default to the first lead time that still fits, once options resolve.
  const selected =
    options.find((option) => option.minutes === selectedMinutes) ?? firstAvailable ?? null;

  // Show the exact moment the chosen reminder fires, so "1 hour before" cannot
  // be misread the way the old "60m" label was.
  let previewAt: string | null = null;
  if (selected && startsAt) {
    previewAt = formatRemindAt(new Date(startsAt).getTime() - selected.minutes * 60_000);
  }

  let body: React.ReactNode;
  if (enabled) {
    body = (
      <>
        <Text variant="mono">Reminder set for {formatRemindAt(reminder.remind_at)}</Text>
        <Button disabled={isLoading} onPress={onCancelReminder} variant="outline">
          <BellOffIcon color="white" size={18} />
          <Text>Cancel reminder</Text>
        </Button>
      </>
    );
  } else if (!firstAvailable) {
    body = <Text variant="muted">This event starts too soon to set a reminder.</Text>;
  } else {
    body = (
      <>
        <Host matchContents>
          <Picker
            appearance="menu"
            onValueChange={(value) => setSelectedMinutes(Number(value) as ReminderIntervalMinutes)}
            selectedValue={String(selected?.minutes ?? firstAvailable.minutes)}
          >
            {options.map((option) => (
              <Picker.Item
                key={option.minutes}
                label={option.available ? option.label : `${option.label} (too late)`}
                value={String(option.minutes)}
              />
            ))}
          </Picker>
        </Host>

        {previewAt && selected?.available ? (
          <Text variant="mono">Notifies {previewAt}</Text>
        ) : (
          <Text variant="muted">
            That lead time has already passed for this run. Pick a shorter one.
          </Text>
        )}

        <Button
          disabled={isLoading || !selected?.available}
          onPress={() => (selected ? onSaveReminder?.(selected.minutes) : undefined)}
        >
          <BellIcon color="white" size={18} />
          <Text>Set reminder</Text>
        </Button>
      </>
    );
  }

  return (
    <View className="gap-3 border-t border-border pt-4">
      <View className="gap-1">
        <Text variant="large">Reminder</Text>
        <Text variant="muted">
          {enabled
            ? 'Your phone will notify you before this run.'
            : 'Choose when your phone should notify you before this run.'}
        </Text>
      </View>

      {body}

      {/*
        Android 12+ downgrades reminders to inexact alarms unless the runner
        grants "Alarms & reminders", which is buried under special app access and
        is not something to expect anyone to find. Declaring the permission is
        not enough on Android 13+, so point at the setting from here.
      */}
      {Platform.OS === 'android' ? (
        <Pressable
          accessibilityHint="Opens this app's system settings"
          accessibilityLabel="Reminders arriving late? Open app settings"
          accessibilityRole="button"
          className="active:opacity-70"
          onPress={() => void Linking.openSettings()}
        >
          <Text variant="muted">
            Arriving a few minutes late? Allow &quot;Alarms &amp; reminders&quot; for 6am BKK in app
            settings, under special app access.
          </Text>
        </Pressable>
      ) : null}

      {error ? <Text className="text-sm font-medium text-destructive">{error}</Text> : null}
    </View>
  );
}

type EventReminderControlsProps = Readonly<{
  event: Pick<Event, 'id' | 'tenant_id' | 'title' | 'starts_at' | 'place_name'>;
  tenantSlug: string;
  timeZone?: string;
  userId: string | null;
}>;

export function EventReminderControls({
  event,
  tenantSlug,
  timeZone,
  userId,
}: EventReminderControlsProps) {
  const eventRemindersEnabled = useReleaseFeatureFlag('eventReminders') === true;

  if (!eventRemindersEnabled) {
    return null;
  }

  if (Platform.OS === 'web') {
    return null;
  }

  return (
    <NativeEventReminderControls
      event={event}
      tenantSlug={tenantSlug}
      timeZone={timeZone}
      userId={userId}
    />
  );
}

function NativeEventReminderControls({
  event,
  tenantSlug,
  timeZone,
  userId,
}: EventReminderControlsProps) {
  const now = useCurrentTime();
  const [reminder, setReminder] = React.useState<EventReminder | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const options = React.useMemo(
    () => reminderOptionStates(event.starts_at, now),
    [event.starts_at, now]
  );

  React.useEffect(() => {
    let active = true;
    if (!userId) {
      return undefined;
    }

    async function load() {
      try {
        const current = await fetchMyEventReminder(event.id);
        if (active) {
          setReminder(current);
        }
      } catch {
        // Reminder controls stay usable even if the preload misses.
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [event.id, userId]);

  async function saveReminder(intervalMinutes: ReminderIntervalMinutes) {
    setIsLoading(true);
    setError(null);
    try {
      const next = await scheduleLocalEventReminder(event, tenantSlug, intervalMinutes, timeZone);
      setReminder(next);
    } catch (reminderError) {
      setError(reminderError instanceof Error ? reminderError.message : 'Could not save reminder.');
    } finally {
      setIsLoading(false);
    }
  }

  async function cancelReminder() {
    if (!reminder) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await cancelLocalEventReminder(reminder);
      setReminder(null);
    } catch (reminderError) {
      setError(
        reminderError instanceof Error ? reminderError.message : 'Could not cancel reminder.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <EventReminderPanel
      error={error}
      isLoading={isLoading}
      onCancelReminder={cancelReminder}
      onSaveReminder={saveReminder}
      options={options}
      reminder={reminder}
      startsAt={event.starts_at}
    />
  );
}
